import { createBlock } from 'factories/block';
import { InteractionProps, repeat } from './interaction';
import { createLayout } from 'factories/layout';
import { colors } from 'styles/variables';
import { Block, cloneBlock, Blocks } from '@chameleoncreator/creator-app';
import { flatten } from 'factories/utils';
import { createContinueBtn } from './continueBtn';
import { createEditableBtn } from './editableBtn';
import { createMediaBlock } from './media';
import Media_default from 'assets/Media_default.svg';
import uuid from 'uuidv4';
import { getSId } from '../utils';
import Correct_default from 'assets/Correct_loop.gif'; // Correct_default.svg';

const { lPink, lGrey0, lGrey1, lGrey2, lTeal0 } = colors;

export type MatchingInteractionProps = InteractionProps & {
  answerCount: number;
};

const createFeedbackBlock = ({ feedbackId }) => {
  return createBlock(
    'Feedback',
    [
      createBlock('Image', [], {
        props: {
          src: Correct_default,
        },
        meta: {
          subType: 'Correct_Feedback',
        },
      }),

      createBlock('Text'),
      createEditableBtn({
        type: 'Feedback_Button_Correct',
        label: 'Continue',
      }),
    ],
    {
      props: { type: 'Correct_Feedback', feedbackId },
      meta: {
        subType: 'Correct_Feedback',
      },
    }
  );
};

const createMatchPairs = (): [
  string, // shared id
  Block, // drag
  Block, // drop
  Block // feedback
] => {
  const id = uuid();
  return [
    id,
    createBlock(
      'MatchDrag',
      [
        createMediaBlock({
          src: undefined,
          child: 1,
        }),
      ],
      {
        props: {
          draggableId: id,
        },
      }
    ),
    createBlock(
      'MatchDrop',
      [
        createMediaBlock({
          src: undefined,
          child: 0,
        }),
      ],
      {
        props: {
          droppableId: id,
        },
      }
    ),
    createFeedbackBlock({
      feedbackId: id,
    }),
  ];
};

export const createMatchingBlock = (count: number) => {
  const DragContainer = createBlock('DragContainer');
  const DropContainer = createBlock('DropContainer');
  const AnswerContainer = createBlock('AnswerContainer');

  Array.from(Array(count)).map(() => {
    const [, dragBlock, dropBlock, feedback] = createMatchPairs();
    DragContainer.children = [
      ...(DragContainer.children as Block[]),
      dragBlock,
    ];
    DropContainer.children = [
      ...(DropContainer.children as Block[]),
      dropBlock,
    ];
    AnswerContainer.children = [
      ...(AnswerContainer.children as Block[]),
      feedback,
    ];
  });

  return createBlock('Matching', [
    DragContainer,
    DropContainer,
    AnswerContainer,
  ]);
};

const contentForColumn = (count: number) => (i: number) => {
  return [() => createBlock('Text', []), () => createMatchingBlock(count)][i];
};

export const addMatchPairs = (
  block: Block,
  position: number,
  blocks: Blocks
): [Block, Blocks?] => {
  if (block.type === 'Matching') {
    const [, dragBlock, dropBlock, feedbackBlock] = createMatchPairs();
    const [
      dragContainerRef,
      dropContainerRef,
      answerContainerRef,
    ] = block.children as Block[];

    const dragContainer = cloneBlock(blocks[dragContainerRef.id]);
    const dropContainer = cloneBlock(blocks[dropContainerRef.id]);
    const answerContainer = cloneBlock(blocks[answerContainerRef.id]);

    const dragRef = {
      id: dragBlock.id,
      type: 'Ref',
    };

    const dropRef = {
      id: dropBlock.id,
      type: 'Ref',
    };

    const feedbackRef = {
      id: feedbackBlock.id,
      type: 'Ref',
    };

    (dragContainer.children as Block[]).splice(position, 0, dragRef);
    (dropContainer.children as Block[]).splice(position, 0, dropRef);
    (answerContainer.children as Block[]).splice(position, 0, feedbackRef);

    const [newBlocksDrag] = flatten(dragBlock);
    const [newBlocksDrop] = flatten(dropBlock);
    const [newBlocksFeedback] = flatten(feedbackBlock);

    const newBlocks = {
      ...newBlocksDrag,
      ...newBlocksDrop,
      ...newBlocksFeedback,
      ...{ [dragContainer.id]: dragContainer },
      ...{ [dropContainer.id]: dropContainer },
      ...{ [answerContainer.id]: answerContainer },
    };

    const nextBlock = cloneBlock(block);
    nextBlock.children = [...(nextBlock.children as Block[])];

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const removeMatchPairs = (
  block: Block,
  position: number,
  blocks: Blocks
): [Block, Blocks?] => {
  if (block.type === 'Matching') {
    const [
      dragContainerRef,
      dropContainerRef,
      answerContainerRef,
    ] = block.children as Block[];

    const dragContainer = cloneBlock(blocks[dragContainerRef.id]);
    const dropContainer = cloneBlock(blocks[dropContainerRef.id]);
    const answerContainer = cloneBlock(blocks[answerContainerRef.id]);

    (dragContainer.children as Block[]).splice(position, 1);
    (dropContainer.children as Block[]).splice(position, 1);
    (answerContainer.children as Block[]).splice(position, 1);

    const newBlocks = {
      ...{ [dragContainer.id]: dragContainer },
      ...{ [dropContainer.id]: dropContainer },
      ...{ [answerContainer.id]: answerContainer },
    };

    const nextBlock = cloneBlock(block);
    nextBlock.children = [...(nextBlock.children as Block[])];

    return [nextBlock, newBlocks];
  }
  return [block, undefined];
};

export const createMatchingInteractionBlock = (
  props: MatchingInteractionProps
) => {
  const {
    panelBackground = '#fff' || lPink,
    panelBackgroundDragging = lGrey0,
    textColorDragging = '#fff',
    dropZoneColor = lGrey1,
    panelWidth = 250,
    panelHeight = 250,
    baseColor = lGrey2,
    lockDown = 0,
    style,
  } = props as any;

  return createBlock(
    'Interaction',
    [
      createLayout(contentForColumn(props.answerCount), 0, true),
      createBlock('SingleColumnLayout', [
        createBlock('Column', [createContinueBtn(style)], {
          props: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          },
          meta: {
            subType: 'ButtonBlock',
          },
        }),
      ]),
    ],
    {
      props: {
        name: props.name,
        sId: getSId('interaction'),
        style: props.style,
        panelBackground,
        panelBackgroundDragging,
        textColorDragging,
        dropZoneColor,
        panelWidth,
        panelHeight,
        baseColor,
        lockDown,
      },
      meta: {
        subType: 'Matching',
        layout: 'OneColumnReversed',
      },
    }
  );
};

export const create = createMatchingInteractionBlock;
export const interactionConfig = {};
